import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectsApi, usersApi } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Plus, Search, FolderKanban, Calendar, Clock, Users,
  ArrowRight, BarChart3, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const TURTLE_MASCOT = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/r0l0976o_Transparent%20BG%20Turtle.png';

const statusConfig = [
  { value: 'todo', label: 'To Do', color: '#1A2B5E' },
  { value: 'in_progress', label: 'In Progress', color: '#106EFD' },
  { value: 'review', label: 'In Review', color: '#E171EA' },
  { value: 'done', label: 'Complete', color: '#37C563' },
];

const priorityStyle = (p) => p === 'high' ? 'bg-red-100 text-red-700' : p === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700';

const getDeadlineState = (dueDate) => {
  if (!dueDate) return 'none';
  const hours = (new Date(dueDate) - new Date()) / (1000 * 60 * 60);
  if (hours < 0) return 'overdue';
  if (hours < 72) return 'urgent';
  if (hours < 168) return 'approaching';
  return 'safe';
};

const Projects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState({
    title: '', description: '', start_date: '', due_date: '',
    priority: 'medium', status: 'todo', assignee_ids: [], tags: []
  });
  const [tagInput, setTagInput] = useState('');

  const fetchData = async () => {
    try {
      const [projRes, usersRes] = await Promise.all([
        projectsApi.getAll(statusFilter !== 'all' ? { status: statusFilter } : {}),
        usersApi.getAll()
      ]);
      setProjects(projRes.data);
      setUsers(usersRes.data);
    } catch { toast.error('Failed to fetch projects'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [statusFilter]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await projectsApi.create(newProject);
      toast.success('Project created!');
      setDialogOpen(false);
      setNewProject({ title: '', description: '', start_date: '', due_date: '', priority: 'medium', status: 'todo', assignee_ids: [], tags: [] });
      setTagInput('');
      fetchData();
    } catch { toast.error('Failed to create project'); }
  };

  const toggleAssignee = (userId) => {
    setNewProject(prev => ({
      ...prev,
      assignee_ids: prev.assignee_ids.includes(userId)
        ? prev.assignee_ids.filter(id => id !== userId)
        : [...prev.assignee_ids, userId]
    }));
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !newProject.tags.includes(tag)) {
      setNewProject({ ...newProject, tags: [...newProject.tags, tag] });
      setTagInput('');
    }
  };

  const filtered = projects.filter(p =>
    !search || p.title?.toLowerCase().includes(search.toLowerCase()) || p.description?.toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total: projects.length,
    active: projects.filter(p => p.status === 'in_progress').length,
    review: projects.filter(p => p.status === 'review').length,
    done: projects.filter(p => p.status === 'done').length,
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="w-10 h-10 loading-spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth']">Projects</h1>
          <p className="text-navy/60 mt-1">Manage larger initiatives and track progress</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90" data-testid="create-project-btn">
              <Plus className="w-4 h-4 mr-2" /> New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-['Amaranth']">Create Project</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2"><Label>Project Title *</Label>
                <Input value={newProject.title} onChange={e => setNewProject({...newProject, title: e.target.value})} placeholder="e.g., Q2 BSA Audit for Beta Bank" required data-testid="project-title-input" /></div>
              <div className="space-y-2"><Label>Description</Label>
                <Textarea value={newProject.description} onChange={e => setNewProject({...newProject, description: e.target.value})} placeholder="What's this project about?" rows={3} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Start Date</Label><Input type="date" value={newProject.start_date} onChange={e => setNewProject({...newProject, start_date: e.target.value})} data-testid="project-start-date" /></div>
                <div className="space-y-2"><Label>Due Date</Label><Input type="date" value={newProject.due_date} onChange={e => setNewProject({...newProject, due_date: e.target.value})} data-testid="project-due-date" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Status</Label>
                  <Select value={newProject.status} onValueChange={v => setNewProject({...newProject, status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
                    {statusConfig.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent></Select></div>
                <div className="space-y-2"><Label>Priority</Label>
                  <Select value={newProject.priority} onValueChange={v => setNewProject({...newProject, priority: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
                    <SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem>
                  </SelectContent></Select></div>
              </div>
              <div className="space-y-2">
                <Label>Assign Team Members</Label>
                <div className="flex flex-wrap gap-2">
                  {users.map(u => (
                    <button key={u.id} type="button" onClick={() => toggleAssignee(u.id)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-all ${newProject.assignee_ids.includes(u.id) ? 'bg-[#40CFDE] text-[#1A2B5E] border-[#40CFDE]' : 'border-navy/20 text-navy/60 hover:border-navy/40'}`}
                      data-testid={`assign-${u.id}`}>
                      {u.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Tags</Label>
                <div className="flex gap-2">
                  <Input value={tagInput} onChange={e => setTagInput(e.target.value)} placeholder="Add a tag" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); }}} className="flex-1" />
                  <Button type="button" variant="outline" size="sm" onClick={addTag}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {newProject.tags.map((tag, i) => (
                    <Badge key={i} className="bg-navy/5 text-navy/70">
                      {tag}
                      <button type="button" onClick={() => setNewProject({...newProject, tags: newProject.tags.filter((_, j) => j !== i)})} className="ml-1 hover:text-red-500">&times;</button>
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#40CFDE] text-[#1A2B5E]" data-testid="submit-project-btn">Create Project</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border-navy/10"><CardContent className="p-5"><p className="text-sm text-navy/60">Total</p><p className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth'] mt-1">{stats.total}</p></CardContent></Card>
        <Card className="border-navy/10"><CardContent className="p-5"><p className="text-sm text-navy/60">Active</p><p className="text-3xl font-bold text-[#106EFD] font-['Amaranth'] mt-1">{stats.active}</p></CardContent></Card>
        <Card className="border-navy/10"><CardContent className="p-5"><p className="text-sm text-navy/60">In Review</p><p className="text-3xl font-bold text-[#E171EA] font-['Amaranth'] mt-1">{stats.review}</p></CardContent></Card>
        <Card className="border-navy/10"><CardContent className="p-5"><p className="text-sm text-navy/60">Complete</p><p className="text-3xl font-bold text-[#37C563] font-['Amaranth'] mt-1">{stats.done}</p></CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
          <Input placeholder="Search projects..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="project-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[170px]" data-testid="project-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {statusConfig.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Project Cards */}
      {filtered.length === 0 ? (
        <Card className="border-navy/10"><CardContent className="py-16 text-center">
          <img src={TURTLE_MASCOT} alt="Turtle" className="w-24 h-24 mx-auto mb-3" />
          <h3 className="text-xl font-['Amaranth'] text-[#1A2B5E] mb-2">No projects yet</h3>
          <p className="text-navy/60">Create your first project to start managing larger initiatives</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(project => {
            const sc = statusConfig.find(s => s.value === project.status) || statusConfig[0];
            const deadline = getDeadlineState(project.due_date);
            const progress = project.task_count > 0 ? Math.round((project.tasks_done / project.task_count) * 100) : 0;
            const assignees = users.filter(u => (project.assignee_ids || []).includes(u.id));

            return (
              <Card
                key={project.id}
                className={`border-navy/10 cursor-pointer hover:shadow-lg transition-all ${deadline === 'overdue' ? 'ring-2 ring-red-300' : deadline === 'urgent' ? 'ring-1 ring-orange-300' : ''}`}
                onClick={() => navigate(`/projects/${project.id}`)}
                data-testid={`project-card-${project.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: sc.color }} />
                      <Badge className={priorityStyle(project.priority)} data-testid={`project-priority-${project.id}`}>{project.priority}</Badge>
                    </div>
                    {deadline === 'overdue' && <AlertTriangle className="w-4 h-4 text-red-500" />}
                  </div>

                  <h3 className="font-semibold text-[#1A2B5E] text-lg mt-3 line-clamp-2">{project.title}</h3>
                  {project.description && <p className="text-sm text-navy/60 mt-1 line-clamp-2">{project.description}</p>}

                  {/* Progress bar */}
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-xs text-navy/50 mb-1">
                      <span>{project.tasks_done}/{project.task_count} tasks</span>
                      <span>{progress}%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: sc.color }} />
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="flex items-center gap-4 mt-4 text-xs text-navy/50">
                    {project.start_date && (
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Start: {new Date(project.start_date).toLocaleDateString()}</span>
                    )}
                    {project.due_date && (
                      <span className={`flex items-center gap-1 ${deadline === 'overdue' ? 'text-red-600 font-semibold' : deadline === 'urgent' ? 'text-orange-600' : ''}`}>
                        <Clock className="w-3 h-3" /> Due: {new Date(project.due_date).toLocaleDateString()}
                        {deadline === 'overdue' && ' (Overdue)'}
                      </span>
                    )}
                  </div>

                  {/* Assignees + Tags */}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-navy/5">
                    <div className="flex -space-x-2">
                      {assignees.slice(0, 4).map(u => (
                        <div key={u.id} className="w-7 h-7 rounded-full bg-[#106EFD] flex items-center justify-center text-white text-xs font-medium border-2 border-white" title={u.name}>
                          {u.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                      ))}
                      {assignees.length > 4 && <div className="w-7 h-7 rounded-full bg-navy/10 flex items-center justify-center text-xs text-navy/60 border-2 border-white">+{assignees.length - 4}</div>}
                      {assignees.length === 0 && <span className="text-xs text-navy/30">Unassigned</span>}
                    </div>
                    <div className="flex gap-1">
                      {(project.tags || []).slice(0, 2).map((tag, i) => <Badge key={i} variant="outline" className="text-[10px]">{tag}</Badge>)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Projects;
